import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Sparkles } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

/**
 * PayMongo (mocked) success landing. The webhook already flipped the
 * subscription on the server; we just refresh the /auth/me payload.
 */
export default function BillingSuccessPage() {
  const [params] = useSearchParams();
  const deepLink = params.get("deep_link");
  const { refreshMe, user } = useAuth();

  useEffect(() => {
    // Give the webhook a moment to arrive at the backend before we
    // refresh /auth/me — otherwise the user object may still show `free`.
    const t = setTimeout(() => refreshMe(), 300);
    return () => clearTimeout(t);
  }, [refreshMe]);

  return (
    <div
      className="mb-page flex min-h-screen items-center justify-center px-6 py-12"
      data-testid="billing-success-page"
    >
      <div className="w-full max-w-lg mb-rise">
        <div className="flex justify-center">
          <BrandLogo className="h-10" />
        </div>
        <div className="mt-12 rounded-2xl border border-gray-200 bg-white p-10 text-center shadow-mb-card">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#35622F]/10">
            <CheckCircle2 className="h-8 w-8 text-[#35622F]" />
          </div>
          <h1 className="mt-6 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            You&apos;re all set.
          </h1>
          <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-gray-500">
            Your payment was received. Your subscription is now active — thank
            you for supporting MatchBroker.
          </p>
          {user?.subscription_status === "pro" && (
            <div className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-[#35622F]/30 bg-[#35622F]/10 px-3 py-1 text-xs font-semibold text-[#35622F]">
              <Sparkles className="h-3.5 w-3.5" />
              Subscription active
            </div>
          )}

          {deepLink && (
            <div className="mt-6 rounded-xl border border-gray-100 bg-gray-50 p-4 text-left">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Mobile deep link
              </div>
              <div
                className="mt-1 break-all font-mono text-xs text-gray-700"
                data-testid="mobile-deep-link"
              >
                {deepLink}
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
            <Link to="/dashboard/billing">
              <Button
                type="button"
                variant="outline"
                className="mb-focus-ring h-11 rounded-xl border-gray-200 bg-white px-6 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                data-testid="success-view-billing"
              >
                View billing
              </Button>
            </Link>
            <Link to="/dashboard/inventory">
              <Button
                type="button"
                className="mb-focus-ring h-11 rounded-xl bg-[#1B2B4B] px-6 text-sm font-semibold text-white hover:bg-[#14213A]"
                data-testid="success-back-to-app"
              >
                Back to MatchBroker
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
